import { MarkButton } from '../../../components/MarkControls';
import React, { useCallback, useEffect, useId, useState, useRef } from 'react';
import * as layout from '../../../components/markLayout.module.css';
import * as journeyStyles from '../StepCanvas/journey.module.css';
type StepChoice =
    | {
          type: 'check_segment';
          activity?: undefined;
      }
    | {
          type: 'activity';
          activity: 'send_email' | 'send_sms';
      }
    | {
          type: 'ab_split';
          activity?: undefined;
      };
type Props = {
    onAdd: (choice: StepChoice) => void;
    disabled?: boolean;
    label?: string;
    compact?: boolean;
    accessibleLabel?: string;
};
const CHOICES: {
    label: string;
    choice: StepChoice;
}[] = [
    { label: 'Check segment', choice: { type: 'check_segment' } },
    { label: 'Send email', choice: { type: 'activity', activity: 'send_email' } },
    { label: 'Send SMS', choice: { type: 'activity', activity: 'send_sms' } },
    { label: 'A/B split', choice: { type: 'ab_split' } }
];
const AddStepMenu: React.FC<Props> = ({
    onAdd,
    disabled,
    label = '+ Add step',
    compact = false,
    accessibleLabel
}) => {
    const [open, setOpen] = useState(false);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const menuRef = useRef<HTMLUListElement | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const positionMenu = useCallback(() => {
        const button = buttonRef.current;
        const menu = menuRef.current;
        if (!button || !menu) return;
        let top = 0;
        let bottom = window.innerHeight;
        let left = 0;
        let right = window.innerWidth;
        for (let parent = button.parentElement; parent; parent = parent.parentElement) {
            const style = window.getComputedStyle(parent);
            const clipsY = /(auto|scroll|hidden|clip)/.test(style.overflowY);
            const clipsX = /(auto|scroll|hidden|clip)/.test(style.overflowX);
            const isDialog = parent.getAttribute('role') === 'dialog';
            if (!clipsX && !clipsY && !isDialog) continue;
            const bounds = parent.getBoundingClientRect();
            if (clipsY || isDialog) {
                top = Math.max(top, bounds.top);
                bottom = Math.min(bottom, bounds.bottom);
            }
            if (clipsX || isDialog) {
                left = Math.max(left, bounds.left);
                right = Math.min(right, bounds.right);
            }
        }
        const bounds = button.getBoundingClientRect();
        const scaleX = bounds.width / button.offsetWidth || 1;
        const scaleY = bounds.height / button.offsetHeight || 1;
        const gap = 4;
        const below = Math.max(0, bottom - bounds.bottom - gap);
        const above = Math.max(0, bounds.top - top - gap);
        menu.style.maxWidth = `${Math.max(0, right - left)}px`;
        const flip = below < menu.scrollHeight && above > below;
        menu.style.transform = `scale(${1 / scaleX}, ${1 / scaleY})`;
        menu.style.transformOrigin = flip ? 'bottom left' : 'top left';
        menu.style.maxHeight = `${flip ? above : below}px`;
        menu.style.top = flip ? 'auto' : `calc(100% + ${gap / scaleY}px)`;
        menu.style.bottom = flip ? `calc(100% + ${gap / scaleY}px)` : 'auto';
        const offset = Math.min(0, right - bounds.left - menu.offsetWidth);
        menu.style.left = `${Math.max(left - bounds.left, offset) / scaleX}px`;
    }, []);
    useEffect(() => {
        if (!open) return;
        const handleOutsidePointer = (event: PointerEvent) => {
            if (event.target instanceof Node && !containerRef.current?.contains(event.target))
                setOpen(false);
        };
        document.addEventListener('pointerdown', handleOutsidePointer, true);
        window.addEventListener('resize', positionMenu);
        document.addEventListener('scroll', positionMenu, true);
        return () => {
            document.removeEventListener('pointerdown', handleOutsidePointer, true);
            window.removeEventListener('resize', positionMenu);
            document.removeEventListener('scroll', positionMenu, true);
        };
    }, [open, positionMenu]);
    const menuId = useId();
    const focusFirstItem = () =>
        requestAnimationFrame(() =>
            menuRef.current?.querySelector<HTMLButtonElement>('[role="menuitem"]')?.focus()
        );
    const closeMenu = () => {
        setOpen(false);
        buttonRef.current?.focus();
    };
    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Escape' && open) {
            event.preventDefault();
            event.stopPropagation();
            closeMenu();
            return;
        }
        if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        if (!open) {
            setOpen(true);
            focusFirstItem();
            return;
        }
        const items = Array.from(
            menuRef.current?.querySelectorAll<HTMLButtonElement>(
                '[role="menuitem"]:not(:disabled)'
            ) ?? []
        );
        if (!items.length) return;
        const index = items.indexOf(document.activeElement as HTMLButtonElement);
        const next =
            event.key === 'Home'
                ? 0
                : event.key === 'End'
                  ? items.length - 1
                  : event.key === 'ArrowDown'
                    ? (index + 1) % items.length
                    : (index - 1 + items.length) % items.length;
        items[next].focus();
        items[next].scrollIntoView({ block: 'nearest' });
    };
    const handleSelect = (choice: StepChoice) => {
        setOpen(false);
        onAdd(choice);
    };
    return (
        <div
            ref={containerRef}
            className={compact ? journeyStyles.addPath : undefined}
            onKeyDown={handleKeyDown}
            onBlur={(event) => {
                if (!event.currentTarget.contains(event.relatedTarget as Node | null))
                    setOpen(false);
            }}
            style={{
                position: 'relative',
                display: 'inline-block'
            }}
        >
            <MarkButton
                ref={buttonRef}
                compact={compact}
                aria-label={accessibleLabel ?? label}
                title={label}
                type="button"
                onMouseDown={(event) => {
                    if (event.button === 0) event.preventDefault();
                }}
                onClick={() => {
                    if (open) closeMenu();
                    else {
                        setOpen(true);
                        focusFirstItem();
                    }
                }}
                disabled={disabled}
                aria-expanded={open}
                aria-haspopup="menu"
                aria-controls={open ? menuId : undefined}
            >
                {compact ? '+' : label}
            </MarkButton>
            {open && (
                <ul
                    id={menuId}
                    ref={(element) => {
                        menuRef.current = element;
                        if (element) positionMenu();
                    }}
                    role="menu"
                    className={layout.addStepMenu}
                >
                    {CHOICES.map((c) => (
                        <li key={c.label} role="none">
                            <MarkButton
                                type="button"
                                role="menuitem"
                                onMouseDown={(event) => {
                                    // Keep Safari blur from closing the menu before click.
                                    if (event.button === 0) event.preventDefault();
                                }}
                                onClick={() => handleSelect(c.choice)}
                                className={layout.addStepMenuItem}
                            >
                                {c.label}
                            </MarkButton>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};
export type { StepChoice };
export default AddStepMenu;
